import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Sparkles, Send, Plus, MessageSquare, Trash2, Users, FileText,
  TrendingUp, Download, Loader2, ChevronRight, Copy, X, Search,
  DollarSign, Calendar as CalendarIcon,
} from "lucide-react";
import { api, API_URL } from "@/lib/api";

const errMsg = (e, fb = "Something went wrong.") => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
  return e?.message || fb;
};

const EXAMPLES = [
  "Create a client Kai Ramirez kai@artist.io — booked a music video for 'Neon Sky', $4500, shooting 2026-03-01 in LA. Email it.",
  "Log a past deal: Sara Chen sara@labelworks.co paid $3200 for full-day studio session on 2026-01-08.",
  "What's this month's revenue?",
  "Update invoice INVOICE_ID_HERE and change amount to 5500.",
  "List all unpaid invoices",
];

export default function AgentAdmin() {
  const [tab, setTab] = useState("chat"); // chat | clients | invoices | revenue
  return (
    <div data-testid="agent-admin">
      <style>{`
        .agent-input {
          width: 100%; padding: 10px 12px; border-radius: 8px;
          background: #0a0a0a; border: 1px solid rgba(255,255,255,0.1);
          color: #fff; font-size: 14px; outline: none;
          transition: border-color 0.15s;
        }
        .agent-input:focus { border-color: rgba(0,184,255,0.5); }
      `}</style>
      <div className="label-mono mb-2">/ AI Assistant</div>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter inline-flex items-center gap-3">
            <Sparkles className="text-cyan-400" size={36}/> Finance Agent
          </h1>
          <p className="text-zinc-500 text-sm mt-2">
            Just tell me what happened — I&apos;ll create clients, draft invoices &amp; contracts, log historical deals, and track revenue.
          </p>
        </div>
        <div className="flex gap-1 p-1 rounded-lg bg-[#121215] border border-white/5">
          {[
            { k: "chat",     label: "Chat",     icon: MessageSquare },
            { k: "clients",  label: "Clients",  icon: Users },
            { k: "invoices", label: "Invoices", icon: FileText },
            { k: "revenue",  label: "Revenue",  icon: TrendingUp },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold tracking-widest transition ${tab === t.k ? "bg-cyan-500/15 text-cyan-300" : "text-zinc-500 hover:text-zinc-300"}`}
              data-testid={`agent-tab-${t.k}`}
            >
              <t.icon size={12}/> {t.label.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {tab === "chat"     && <Chat/>}
      {tab === "clients"  && <ClientsList/>}
      {tab === "invoices" && <InvoicesList/>}
      {tab === "revenue"  && <RevenueView/>}
    </div>
  );
}

/* ---------- Chat ---------- */
function Chat() {
  const [sessions, setSessions] = useState([]);
  const [sid, setSid] = useState(null);
  const [session, setSession] = useState(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [pending, setPending] = useState(null); // optimistic user msg while waiting
  const listRef = useRef(null);

  const loadSessions = async () => {
    try {
      const r = await api.get("/admin/agent/sessions");
      setSessions(r.data || []);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const loadSession = async (id) => {
    if (!id) { setSession(null); return; }
    try {
      const r = await api.get(`/admin/agent/sessions/${id}`);
      setSession(r.data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { loadSession(sid); }, [sid]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [session?.messages?.length, pending]);

  const startNew = () => { setSid(null); setSession(null); setMsg(""); };

  const send = async () => {
    const text = msg.trim();
    if (!text || sending) return;
    setSending(true);
    setPending(text);
    setMsg("");
    try {
      const r = await api.post("/admin/agent/chat", { session_id: sid, message: text });
      setPending(null);
      if (!sid) setSid(r.data.session_id);
      await loadSession(r.data.session_id || sid);
      loadSessions();
    } catch (e) {
      setPending(null);
      toast.error(errMsg(e, "Chat failed."));
    } finally {
      setSending(false);
    }
  };

  const deleteSession = async (id) => {
    if (!window.confirm("Delete this chat?")) return;
    await api.delete(`/admin/agent/sessions/${id}`);
    if (sid === id) startNew();
    loadSessions();
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const messages = session?.messages || [];

  return (
    <div className="grid lg:grid-cols-[260px_1fr] gap-4 h-[calc(100vh-260px)] min-h-[520px]">
      {/* Sessions sidebar */}
      <div className="rounded-2xl bg-[#121215] border border-white/5 flex flex-col overflow-hidden">
        <div className="p-3 border-b border-white/5 flex items-center justify-between">
          <div className="label-mono">/ chats</div>
          <button onClick={startNew} data-testid="agent-new-session"
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-cyan-500/15 text-cyan-300 hover:bg-cyan-500/25 text-[10px] font-black tracking-widest">
            <Plus size={10}/> NEW
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {sessions.length === 0 && (
            <div className="p-4 text-center text-zinc-500 text-xs">No chats yet.</div>
          )}
          {sessions.map((s) => (
            <div key={s.id} className={`group flex items-center gap-1 rounded-lg px-2 mb-1 ${sid === s.id ? "bg-cyan-500/10" : "hover:bg-white/[0.03]"}`}>
              <button onClick={() => setSid(s.id)}
                      className={`flex-1 text-left py-2 text-sm truncate ${sid === s.id ? "text-cyan-200" : "text-zinc-300"}`}
                      data-testid={`agent-session-${s.id}`}>
                {s.title}
              </button>
              <button onClick={() => deleteSession(s.id)}
                      className="opacity-0 group-hover:opacity-100 text-red-400/70 hover:text-red-300 px-1">
                <Trash2 size={12}/>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Chat area */}
      <div className="rounded-2xl bg-[#121215] border border-white/5 flex flex-col overflow-hidden">
        <div ref={listRef} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="agent-messages">
          {messages.length === 0 && !pending && (
            <div className="max-w-lg mx-auto text-center pt-8">
              <Sparkles className="text-cyan-400 mx-auto mb-4" size={36}/>
              <div className="font-display text-2xl font-bold mb-2">Hey — what happened today?</div>
              <div className="text-zinc-500 text-sm mb-6">
                Tell me about a new client, a past deal, or ask about your revenue.
              </div>
              <div className="space-y-2 text-left">
                {EXAMPLES.map((ex, i) => (
                  <button key={i} onClick={() => setMsg(ex)}
                          className="w-full p-3 rounded-lg bg-black/40 border border-white/5 hover:border-cyan-500/40 text-sm text-zinc-300 text-left transition">
                    <ChevronRight size={12} className="inline text-cyan-400 mr-1"/> {ex}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <MessageBubble key={i} msg={m}/>
          ))}
          {pending && (
            <>
              <MessageBubble msg={{ role: "user", content: pending }}/>
              <div className="flex items-center gap-2 text-cyan-300 text-sm">
                <Loader2 className="animate-spin" size={14}/> thinking…
              </div>
            </>
          )}
        </div>
        <div className="p-4 border-t border-white/5 bg-black/40">
          <div className="flex gap-2 items-end">
            <textarea
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              onKeyDown={onKey}
              placeholder="Describe a client, deal, or ask about revenue…"
              rows={2}
              className="agent-input resize-none"
              disabled={sending}
              data-testid="agent-chat-input"
            />
            <button onClick={send} disabled={sending || !msg.trim()}
                    className="px-5 py-3 rounded-full bg-cyan-400 text-black text-xs font-black tracking-widest disabled:opacity-40 inline-flex items-center gap-2 whitespace-nowrap"
                    data-testid="agent-chat-send">
              {sending ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>} SEND
            </button>
          </div>
          <div className="text-[10px] text-zinc-500 mt-2 tracking-widest">
            SHIFT+ENTER FOR NEWLINE · CLAUDE SONNET 4.5
          </div>
        </div>
      </div>
    </div>
  );
}

function MessageBubble({ msg }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${isUser ? "bg-cyan-500/15 text-cyan-100" : "bg-white/[0.04] text-zinc-100"}`}>
        {msg.content || <span className="text-zinc-500 italic">(action only)</span>}
        {msg.actions?.length > 0 && (
          <div className="mt-3 space-y-1.5">
            {msg.actions.map((a, i) => <ActionPill key={i} action={a}/>)}
          </div>
        )}
      </div>
    </div>
  );
}

function ActionPill({ action }) {
  const { tool, result } = action;
  const label = {
    create_client: "Client saved",
    search_clients: "Clients found",
    create_invoice_and_contract: result?.sent ? "Invoice sent" : "Invoice drafted",
    list_invoices: "Invoices listed",
    update_invoice: "Invoice updated",
    record_historical_transaction: "Historical deal logged",
    get_revenue_summary: "Revenue summary",
    list_transactions: "Transactions listed",
    delete_transaction: "Transaction deleted",
    update_client: "Client updated",
  }[tool] || tool;

  const isOk = result?.ok !== false && !result?.error;
  const signUrl = result?.sign_url;

  return (
    <div className={`inline-flex flex-wrap items-center gap-2 px-2.5 py-1.5 rounded-md text-[11px] font-mono tracking-wider border ${isOk ? "border-emerald-400/30 bg-emerald-400/5 text-emerald-200" : "border-red-400/30 bg-red-400/5 text-red-200"}`}>
      <span className="font-black tracking-widest">{label.toUpperCase()}</span>
      {result?.amount != null && <span>${Number(result.amount).toLocaleString()}</span>}
      {result?.invoice_id && <span className="text-[10px] opacity-60">#{String(result.invoice_id).slice(0,8)}</span>}
      {signUrl && (
        <button onClick={() => { navigator.clipboard.writeText(signUrl); toast.success("Sign URL copied"); }}
                className="inline-flex items-center gap-1 text-[10px] hover:underline">
          <Copy size={9}/> COPY URL
        </button>
      )}
      {result?.error && <span className="text-[10px]">— {String(result.error).slice(0, 80)}</span>}
    </div>
  );
}

/* ---------- Clients list ---------- */
function ClientsList() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const load = async (query = "") => {
    const r = await api.get("/admin/clients", { params: { q: query } });
    setRows(r.data.rows);
  };
  useEffect(() => { load(); }, []);

  const del = async (c) => {
    if (!window.confirm(`Remove ${c.name}?`)) return;
    await api.delete(`/admin/clients/${c.id}`);
    toast.success("Removed.");
    load(q);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500"/>
          <input value={q} onChange={(e) => { setQ(e.target.value); load(e.target.value); }}
                 placeholder="Search clients…"
                 className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-[#121215] border border-white/10 text-sm outline-none focus:border-cyan-500/40"
                 data-testid="clients-search"/>
        </div>
        <button onClick={() => setShowAdd(true)}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-cyan-400 text-black text-xs font-black tracking-widest"
                data-testid="clients-add">
          <Plus size={12}/> ADD CLIENT
        </button>
      </div>
      <div className="rounded-2xl border border-white/5 bg-[#121215] overflow-hidden">
        {!rows ? (
          <div className="p-12 text-center text-zinc-500 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-zinc-500 text-sm">No clients yet. Ask the agent to create one.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] tracking-widest text-zinc-500 border-b border-white/5">
                <th className="text-left px-5 py-3 font-mono">Name</th>
                <th className="text-left px-5 py-3 font-mono">Email</th>
                <th className="text-left px-5 py-3 font-mono">Company</th>
                <th className="text-left px-5 py-3 font-mono">Added</th>
                <th className="text-right px-5 py-3 font-mono">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-5 py-3 font-semibold">{c.name}</td>
                  <td className="px-5 py-3 text-zinc-400 text-xs font-mono">{c.email}</td>
                  <td className="px-5 py-3 text-zinc-500 text-xs">{c.company || "—"}</td>
                  <td className="px-5 py-3 text-zinc-500 text-xs font-mono">{new Date(c.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => del(c)} className="text-red-400/70 hover:text-red-300">
                      <Trash2 size={14}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <AnimatePresence>
        {showAdd && <AddClientModal onClose={() => setShowAdd(false)} onDone={() => load(q)}/>}
      </AnimatePresence>
    </div>
  );
}

function AddClientModal({ onClose, onDone }) {
  const [f, setF] = useState({ name: "", email: "", company: "", phone: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/clients", f);
      toast.success("Client saved.");
      onDone(); onClose();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };
  return (
    <Modal onClose={onClose} title="Add Client">
      <form onSubmit={save} className="space-y-3">
        <input required placeholder="Name" className="agent-input"
               value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}/>
        <input required type="email" placeholder="Email" className="agent-input"
               value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })}/>
        <input placeholder="Company (optional)" className="agent-input"
               value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })}/>
        <input placeholder="Phone (optional)" className="agent-input"
               value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })}/>
        <textarea rows={3} placeholder="Notes (optional)" className="agent-input"
                  value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })}/>
        <button disabled={saving} className="w-full px-5 py-3 rounded-full bg-cyan-400 text-black text-xs font-black tracking-widest disabled:opacity-50">
          {saving ? "SAVING…" : "SAVE CLIENT"}
        </button>
      </form>
    </Modal>
  );
}

/* ---------- Invoices list ---------- */
function InvoicesList() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("");

  const load = async () => {
    const r = await api.get("/admin/invoices", { params: status ? { status } : {} });
    setRows(r.data);
  };
  useEffect(() => { load(); }, [status]);

  const statusColor = (s) => ({
    draft:     "bg-zinc-500/20 text-zinc-400",
    sent:      "bg-cyan-500/15 text-cyan-300",
    signed:    "bg-emerald-500/15 text-emerald-300",
    completed: "bg-emerald-400/25 text-emerald-200",
  }[s] || "bg-white/5 text-zinc-400");

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-4">
        {["", "draft", "sent", "signed", "completed"].map((s) => (
          <button key={s || "all"} onClick={() => setStatus(s)}
                  className={`px-3 py-1.5 rounded-full text-[10px] font-black tracking-widest transition ${status === s ? "bg-cyan-400 text-black" : "bg-white/5 text-zinc-400 hover:text-white"}`}
                  data-testid={`invoice-filter-${s || "all"}`}>
            {(s || "all").toUpperCase()}
          </button>
        ))}
      </div>
      <div className="rounded-2xl border border-white/5 bg-[#121215] overflow-hidden">
        {!rows ? (
          <div className="p-12 text-center text-zinc-500 text-sm">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-12 text-center text-zinc-500 text-sm">No invoices yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] tracking-widest text-zinc-500 border-b border-white/5">
                <th className="text-left px-5 py-3 font-mono">Project</th>
                <th className="text-left px-5 py-3 font-mono">Client</th>
                <th className="text-left px-5 py-3 font-mono">Amount</th>
                <th className="text-left px-5 py-3 font-mono">Status</th>
                <th className="text-left px-5 py-3 font-mono">Created</th>
                <th className="text-right px-5 py-3 font-mono">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-5 py-3">{r.project_title}</td>
                  <td className="px-5 py-3"><div>{r.client_name}</div><div className="text-zinc-500 text-xs">{r.client_email}</div></td>
                  <td className="px-5 py-3 font-mono">${(r.total || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-black tracking-widest ${statusColor(r.status)}`}>
                      {(r.status || "").toUpperCase()}
                    </span>
                    {r.historical && <span className="ml-1 text-[9px] text-zinc-500">HIST</span>}
                  </td>
                  <td className="px-5 py-3 text-zinc-500 text-xs font-mono">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-2 justify-end">
                      <a href={`${API_URL}/admin/contracts/${r.id}/pdf`} target="_blank" rel="noopener noreferrer"
                         className="inline-flex items-center gap-1 text-cyan-300 hover:text-cyan-200 text-[10px] font-black tracking-widest">
                        <Download size={11}/> PDF
                      </a>
                      {(r.historical || r.status === "draft") && (
                        <button onClick={async () => {
                          if (!window.confirm(`Delete "${r.project_title}"? Only draft/historical entries can be removed.`)) return;
                          try { await api.delete(`/admin/invoices/${r.id}`); toast.success("Deleted."); load(); }
                          catch (e) { toast.error(errMsg(e)); }
                        }} className="text-red-400/70 hover:text-red-300" title="Delete">
                          <Trash2 size={13}/>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ---------- Revenue view ---------- */
function RevenueView() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/revenue/monthly").then((r) => setData(r.data)); }, []);
  const months = data?.months || [];
  const grand = data?.grand_total || 0;
  const monthLabel = (ym) => {
    try { return new Date(ym + "-01T00:00:00Z").toLocaleDateString(undefined, { year: "numeric", month: "long" }); }
    catch { return ym; }
  };
  const maxRev = useMemo(() => Math.max(1, ...months.map((m) => m.revenue)), [months]);

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <div className="label-mono">Total collected</div>
          <div className="font-display text-5xl font-bold gradient-text tracking-tighter">${grand.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
        </div>
        <a href={`${API_URL}/admin/revenue/monthly.pdf`} target="_blank" rel="noopener noreferrer"
           className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-cyan-400 text-black text-xs font-black tracking-widest hover:scale-[1.02] transition"
           data-testid="revenue-pdf-btn">
          <Download size={12}/> DOWNLOAD PDF
        </a>
      </div>
      <div className="rounded-2xl border border-white/5 bg-[#121215] overflow-hidden">
        {!data ? (
          <div className="p-12 text-center text-zinc-500 text-sm">Loading…</div>
        ) : months.length === 0 ? (
          <div className="p-12 text-center text-zinc-500 text-sm">No revenue recorded yet.</div>
        ) : (
          <ul className="divide-y divide-white/5">
            {months.map((m) => (
              <li key={m.month} className="p-5 flex items-center gap-4">
                <div className="min-w-[160px]">
                  <div className="font-display text-lg font-bold">{monthLabel(m.month)}</div>
                  <div className="text-zinc-500 text-xs">{m.count} payment{m.count === 1 ? "" : "s"}</div>
                </div>
                <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-cyan-500 to-cyan-300" style={{ width: `${(m.revenue / maxRev) * 100}%` }}/>
                </div>
                <div className="text-right min-w-[120px]">
                  <div className="font-mono text-lg font-black">${m.revenue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/* ---------- Modal shell ---------- */
function Modal({ onClose, title, children }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.94, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, y: 20 }}
                  className="w-full max-w-md rounded-2xl bg-[#121215] border border-white/10 p-6"
                  onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div className="font-display text-2xl font-bold tracking-tight">{title}</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={18}/></button>
        </div>
        {children}
      </motion.div>
    </motion.div>
  );
}
